/**
 * CÓDIGO DE BARRAS CODE 128 — desenhado aqui, em SVG, sem biblioteca.
 *
 * POR QUE ISTO EXISTE: quando a caixa de seringas chega sem etiqueta, quem
 * numera é o sistema (SER-0051, SER-0052...). Só que um número que existe
 * apenas na tela não serve para nada — a seringa sai e volta BIPANDO. Sem a
 * etiqueta impressa com o código de barras, aquelas seringas nunca poderiam
 * ser lidas, e o cadastro em lote seria uma lista bonita e inútil.
 *
 * É o mesmo simbolismo que o leitor da casa já lê na conferência de material
 * (o ZXing de src/leitor-camera.ts lê Code 128 nativamente), então a etiqueta
 * impressa aqui funciona tanto no leitor de mão quanto na câmera do celular.
 *
 * Só o CODE 128-B (o conjunto com letras, números e pontuação comum): o
 * código de uma seringa é curto e tem hífen. O modo C, que comprime pares de
 * dígitos, renderia uma etiqueta um pouco menor e traria uma máquina de
 * estados inteira junto — não paga.
 */

/**
 * As 107 combinações do Code 128: cada uma tem 6 elementos (barra, espaço,
 * barra, espaço, barra, espaço) e cada dígito é a LARGURA em módulos. A última
 * (o STOP) tem 7 — é a única assimétrica, e é assim na norma.
 *
 * A tabela é copiada da especificação e não se mexe nela: um dígito trocado
 * aqui vira um código que imprime bonito e nenhum leitor lê.
 */
const PADROES = [
  "212222", "222122", "222221", "121223", "121322", "131222", "122213", "122312",
  "132212", "221213", "221312", "231212", "112232", "122132", "122231", "113222",
  "123122", "123221", "223211", "221132", "221231", "213212", "223112", "312131",
  "311222", "321122", "321221", "312212", "322112", "322211", "212123", "212321",
  "232121", "111323", "131123", "131321", "112313", "132113", "132311", "211313",
  "231113", "231311", "112133", "112331", "132131", "113123", "113321", "133121",
  "313121", "211331", "231131", "213113", "213311", "213131", "311123", "311321",
  "331121", "312113", "312311", "332111", "314111", "221411", "431111", "111224",
  "111422", "121124", "121421", "141122", "141221", "112214", "112412", "122114",
  "122411", "142112", "142211", "241211", "221114", "413111", "241112", "134111",
  "111242", "121142", "121241", "114212", "124112", "124211", "411212", "421112",
  "421211", "212141", "214121", "412121", "111143", "111341", "131141", "114113",
  "114311", "411113", "411311", "113141", "114131", "311141", "411131", "211412",
  "211214", "211232", "2331112",
];

const START_B = 104;
const STOP = 106;

/** O código cabe no Code 128-B? (ASCII imprimível, do espaço ao "~") */
export function podeVirarBarras(texto: string): boolean {
  return texto.length > 0 && [...texto].every((c) => {
    const n = c.charCodeAt(0);
    return n >= 32 && n <= 126;
  });
}

/**
 * As larguras de todas as barras e espaços do código, na ordem em que são
 * desenhadas (começa sempre por barra).
 */
function larguras(texto: string): number[] {
  const valores = [START_B, ...[...texto].map((c) => c.charCodeAt(0) - 32)];

  /*
   * O DÍGITO VERIFICADOR é a soma ponderada pela POSIÇÃO (o start pesa 1, o
   * primeiro caractere pesa 1, o segundo 2...), módulo 103. Errar esse peso é
   * o jeito clássico de imprimir um código que só falha em alguns valores —
   * por isso o índice começa em 1 no laço abaixo, e não em 0.
   */
  let soma = START_B;
  for (let i = 1; i < valores.length; i++) soma += valores[i]! * i;
  valores.push(soma % 103, STOP);

  const saida: number[] = [];
  for (const v of valores) {
    for (const d of PADROES[v]!) saida.push(Number(d));
  }
  return saida;
}

export interface OpcoesBarras {
  /** Largura de 1 módulo, em px. 2 dá uma etiqueta que qualquer leitor pega. */
  modulo?: number;
  /** Altura das barras, em px. */
  altura?: number;
  /** Escrever o código por extenso embaixo (é o que salva quando a tinta falha). */
  legenda?: boolean;
}

/**
 * O SVG do código de barras, pronto para entrar num `innerHTML`.
 *
 * A "zona muda" (margem branca dos dois lados) tem 10 módulos de propósito:
 * é exigência do simbolismo. Sem ela o leitor não acha o começo do código e a
 * etiqueta simplesmente não lê — sintoma que parece "impressora ruim".
 */
export function code128SVG(texto: string, opcoes: OpcoesBarras = {}): string {
  const modulo = opcoes.modulo ?? 2;
  const altura = opcoes.altura ?? 56;
  const legenda = opcoes.legenda !== false;
  if (!podeVirarBarras(texto)) return "";

  const barras = larguras(texto);
  const margem = 10;
  const total = barras.reduce((s, n) => s + n, 0) + margem * 2;
  const alturaLegenda = legenda ? 16 : 0;

  let x = margem;
  let retangulos = "";
  barras.forEach((largura, i) => {
    // Índice par = barra (preta); ímpar = espaço. É o que alterna o desenho.
    if (i % 2 === 0) {
      retangulos += `<rect x="${x * modulo}" y="0" width="${largura * modulo}" height="${altura}" />`;
    }
    x += largura;
  });

  const largura = total * modulo;
  const texto_ = legenda
    ? `<text x="${largura / 2}" y="${altura + 13}" text-anchor="middle" ` +
      `font-family="Consolas,'Courier New',monospace" font-size="13" letter-spacing="1" ` +
      `fill="#000">${texto.replace(/[<&>]/g, "")}</text>`
    : "";

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${largura}" height="${altura + alturaLegenda}" ` +
    `viewBox="0 0 ${largura} ${altura + alturaLegenda}" shape-rendering="crispEdges">` +
    `<rect width="100%" height="100%" fill="#fff" />` +
    `<g fill="#000">${retangulos}</g>${texto_}</svg>`
  );
}

/**
 * Texto livre dentro do HTML da folha. A legenda é o NOME do equipamento, que
 * pode ter "&" ou aspas ("Megôhmetro 5 kV & acessórios") — apagar esses
 * caracteres, como se faz com o código, mudaria o nome impresso.
 */
function escaparHtml(texto: string): string {
  return texto
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * A FOLHA DE ETIQUETAS pronta para Ctrl+P → "Salvar como PDF".
 *
 * Sai como HTML numa janela nova (o mesmo caminho dos documentos dos Correios,
 * em components/MaterialConferencia.tsx) em vez de um PDF gerado no servidor:
 * a impressora de etiqueta de cada bancada tem um tamanho, e a janela de
 * impressão do navegador é onde isso se resolve sem release.
 *
 * `legendas` (opcional) é uma linha POR CÓDIGO, na mesma ordem — o nome do
 * equipamento de ensaio em cima das barras, para ninguém colar a etiqueta do
 * megôhmetro no terrômetro. Sem ela (as seringas), a folha sai como sempre.
 */
export function htmlEtiquetas(
  codigos: string[],
  titulo: string,
  subtitulo = "",
  legendas?: string[],
): string {
  const etiquetas = codigos
    .map((c, i) => {
      const legenda = String(legendas?.[i] ?? "").trim();
      return (
        `<div class="et">` +
        (legenda ? `<div class="leg">${escaparHtml(legenda)}</div>` : "") +
        `${code128SVG(c, { modulo: 2, altura: 48 })}` +
        `<div class="sub">${subtitulo.replace(/[<&>]/g, "")}</div></div>`
      );
    })
    .join("");

  return `<!doctype html>
<html lang="pt-BR"><head><meta charset="utf-8" />
<title>${titulo.replace(/[<&>]/g, "")}</title>
<style>
  @page { margin: 8mm; }
  body { font-family: system-ui, Segoe UI, sans-serif; margin: 0; padding: 10mm 6mm; color: #13241b; }
  h1 { font-size: 15px; margin: 0 0 2px; }
  p.cab { font-size: 12px; color: #5e7268; margin: 0 0 12px; }
  .folha { display: flex; flex-wrap: wrap; gap: 6mm; }
  .et {
    border: 1px dashed #bbb; border-radius: 4px; padding: 4mm 3mm 3mm;
    display: flex; flex-direction: column; align-items: center; gap: 2px;
    page-break-inside: avoid; break-inside: avoid;
  }
  .et .sub { font-size: 9px; color: #444; max-width: 46mm; text-align: center; }
  .et .leg { font-size: 11px; font-weight: 700; max-width: 52mm; text-align: center; line-height: 1.2; overflow-wrap: anywhere; }
  .aviso { font-size: 11px; color: #5e7268; margin-top: 12px; }
  @media print { .aviso { display: none; } .et { border-color: #ddd; } }
</style></head>
<body>
  <h1>${titulo.replace(/[<&>]/g, "")}</h1>
  <p class="cab">${codigos.length} etiqueta(s)${subtitulo ? ` · ${subtitulo.replace(/[<&>]/g, "")}` : ""}</p>
  <div class="folha">${etiquetas}</div>
  <p class="aviso">
    Use Ctrl+P e “Salvar como PDF” (ou mande direto para a impressora de etiquetas).
    Confira uma etiqueta no leitor antes de imprimir a folha inteira.
  </p>
</body></html>`;
}
